/**
 * base-color-set
 */
export const baseColors: string[] = [
  //'#246090',
  //'#2B77B3',
  //'#388DD0',
  //'#5AA1D9',
  //'#7CB5E2',
  //'#9EC8EA',
  '#1B9E77',
  '#D95F02',
  '#7570B3',
  '#E7298A',
  '#66A61E',
  '#E6AB02',
  '#A6761D',
  '#666666',
];

/**
 * Converts a hex color to rgba format.
 *
 * @param {string} hex - The hex color code (e.g., "#ff5733" or "#f53").
 * @param {number} opacity - The opacity value (0 to 1).
 * @returns {string} The rgba color string.
 */
export function hexToRgba(hex: string, opacity = 0.8): string {
  // Remove the hash (#) if present
  hex = hex.replace(/^#/, '');

  // Handle shorthand hex codes (e.g., #f53 => #ff5533)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  // Parse the r, g, and b values
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Determines the appropriate text color ("text-black" or "text-white") based on the brightness of the provided background color.
 *
 * @param {string} [backgroundColor] - The background color in hexadecimal format (e.g., "#FFFFFF"). If not provided or invalid, defaults to "text-black".
 * @return {'text-black' | 'text-white'} Returns "text-black" if the background color is bright, and "text-white" if it is dark.
 */
export function getTextColorBasedOnBackground(
  backgroundColor?: string
): 'text-black' | 'text-white' {
  if (!backgroundColor) return 'text-black';
  const hex = backgroundColor;
  // Convert hex to RGB
  const rgb = colorToRgb(hex);

  if (!rgb) return 'text-black'; // Default to black if color is invalid

  // Calculate brightness
  const brightness = Math.sqrt(
    0.299 * Math.pow(rgb.r, 2) +
      0.587 * Math.pow(rgb.g, 2) +
      0.114 * Math.pow(rgb.b, 2)
  );

  // Return black or white based on brightness threshold
  return brightness > 128 ? 'text-black' : 'text-white';
}
export function colorToRgb(
  color: string
): { r: number; g: number; b: number; a?: number } | null {
  if (!color) return null;

  // Handle hex color (e.g., #RRGGBB, #RGB)
  if (color.startsWith('#')) {
    const sanitizedHex = color.replace(/^#/, '');
    if (sanitizedHex.length === 3) {
      // Expand shorthand form (#123 -> #112233)
      const [r, g, b] = sanitizedHex.split('').map(h => parseInt(h + h, 16));
      return { r, g, b };
    } else if (sanitizedHex.length === 6) {
      const r = parseInt(sanitizedHex.substring(0, 2), 16);
      const g = parseInt(sanitizedHex.substring(2, 4), 16);
      const b = parseInt(sanitizedHex.substring(4, 6), 16);
      return { r, g, b };
    }
    return null; // Invalid hex string
  }

  // Handle rgb() and rgba() strings
  const rgbaMatch =
    /rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d*(?:\.\d+)?)?\)/i.exec(color);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : undefined; // Alpha is optional
    return { r, g, b, a };
  }

  return null; // Unsupported format
}

/**
 * Generates a new color based on a list of existing colors and an index, ensuring variety and relative contrast.
 *
 * @param {string[]} existingColors - An array of existing color strings in hexadecimal format.
 * @param {number} idx - The index used to determine the hue shift and selection from the existing colors.
 * @return {string} A newly generated color in hexadecimal format.
 */
export function generateColor(existingColors: string[], idx: number): string {
  if (existingColors.length === 0) {
    return '#CCCCCC'; // Fallback neutral gray
  }

  // Get the base color for blending
  const baseColor = existingColors[idx % existingColors.length];

  // Convert the base color to HSL
  const hsl = hexToHsl(baseColor);

  // Calculate a hue shift based on the index
  const hueShift = (idx * 45) % 360; // Rotate hue for variety
  const newHue = (hsl.h + hueShift) % 360; // Ensure hue stays within bounds

  // Adjust saturation and lightness relative to the original color
  const newSaturation = Math.max(50, hsl.s); // Keep saturation strong (minimum 50%)
  const newLightness = Math.max(30, Math.min(70, hsl.l - 5)); // Clamp lightness to prevent overly light colors

  // Create the new HSL color
  const newColor = hslToHex({
    h: newHue,
    s: newSaturation,
    l: newLightness,
  });

  return newColor;
}

/**
 * Converts a hexadecimal color code to an HSL (Hue, Saturation, Lightness) representation.
 *
 * @param {string} hex - A hexadecimal color code in the format "#RRGGBB" or "RRGGBB".
 * @return {{ h: number, s: number, l: number }} An object representing the HSL color,
 * where `h` is the hue in degrees (0-360), `s` is the saturation as a percentage (0-100),
 * and `l` is the lightness as a percentage (0-100).
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  // Remove the '#' if it exists
  hex = hex.replace(/^#/, '');

  // Parse the hex color
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  // Convert to HSL
  const rNormalized = r / 255;
  const gNormalized = g / 255;
  const bNormalized = b / 255;

  const max = Math.max(rNormalized, gNormalized, bNormalized);
  const min = Math.min(rNormalized, gNormalized, bNormalized);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rNormalized:
        h =
          ((gNormalized - bNormalized) / delta +
            (gNormalized < bNormalized ? 6 : 0)) %
          6;
        break;
      case gNormalized:
        h = (bNormalized - rNormalized) / delta + 2;
        break;
      case bNormalized:
        h = (rNormalized - gNormalized) / delta + 4;
        break;
    }
    h *= 60;
  }

  return { h, s: s * 100, l: l * 100 };
}

// Utility: Convert HSL color to HEX
export function hslToHex(hsl: { h: number; s: number; l: number }): string {
  const { h, s, l } = hsl;

  const c = (1 - Math.abs((2 * l) / 100 - 1)) * (s / 100);
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l / 100 - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (value: number) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Adjust the alpha value of an RGBA color string or an array of strings.
 *
 * @param rgbaColors - A single RGBA color string (e.g., "rgba(223, 15, 210, 0.3)")
 *                     or an array of RGBA color strings (e.g., ["rgba(223, 15, 210, 0.3)", "rgba(100, 100, 100, 0.5)"]).
 * @param newAlpha - The new alpha value to be used for the output (e.g., 0.7 for a darker color).
 * @returns The updated single RGBA color string or an array of updated RGBA strings.
 */
export function updateAlpha(
  rgbaColors: string | string[],
  newAlpha: number
): string | string[] {
  // Function to update alpha for a single RGBA string
  const updateSingleAlpha = (rgbaColor: string): string => {
    const rgbaPattern = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/;
    const match = rgbaColor.match(rgbaPattern);

    if (!match) {
      throw new Error(`Invalid RGBA color format: ${rgbaColor}`);
    }

    const [_, r, g, b] = match; // Extract RGB values
    return `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
  };

  // If input is an array, map through each color and update the alpha value
  if (Array.isArray(rgbaColors)) {
    return rgbaColors.map(color => updateSingleAlpha(color));
  }

  // Otherwise, treat it as a single RGBA string
  return updateSingleAlpha(rgbaColors);
}

/**
 * Returns the opposite color of a given hex color.
 * @param {string} hex - The hex value of the color (e.g., "#RRGGBB").
 * @returns {string} - The hex value of the opposite color.
 */
export function getOppositeColor(hex: string): string {
  // Remove the "#" if it exists
  hex = hex.replace('#', '');

  // Parse the R, G, B values from the hex string
  const r = parseInt(hex.substring(0, 2), 16); // Red
  const g = parseInt(hex.substring(2, 4), 16); // Green
  const b = parseInt(hex.substring(4, 6), 16); // Blue

  // Calculate the opposite color
  const oppositeR = 255 - r;
  const oppositeG = 255 - g;
  const oppositeB = 255 - b;

  // Convert back to a hex string and return
  const oppositeHex =
    '#' +
    oppositeR.toString(16).padStart(2, '0') +
    oppositeG.toString(16).padStart(2, '0') +
    oppositeB.toString(16).padStart(2, '0');

  return oppositeHex;
}

/**
 * Generates distinguishable yet harmonious colors from a given array of hex colors.
 * @param {string[]} colorsArray - An array of hex color strings (e.g., ["#3498db", "#2ecc71"]).
 * @returns {string[]} - An array of new harmonious and distinct hex colors.
 */
export function generateHarmoniousColors(colorsArray: string[]) {
  // Helper: Convert Hex to HSL
  const hexToHsl = (hex: string) => {
    // Remove the "#" and parse the RGB values
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0; // Default to 0° hue
    if (delta === 0)
      h = 0; // No difference
    else if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    return { h, s: s * 100, l: l * 100 };
  };

  // Helper: Convert HSL to Hex
  const hslToHex = ({ h, s, l }: { h: number; s: number; l: number }) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r: number | string = 0,
      g: number | string = 0,
      b: number | string = 0;

    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    r = Math.round((r + m) * 255)
      .toString(16)
      .padStart(2, '0');
    g = Math.round((g + m) * 255)
      .toString(16)
      .padStart(2, '0');
    b = Math.round((b + m) * 255)
      .toString(16)
      .padStart(2, '0');

    return `#${r}${g}${b}`;
  };

  // Create a new array of harmonious colors
  const generatedColors = colorsArray.map((hex, index) => {
    const hsl = hexToHsl(hex);

    // Generate harmonious color by shifting the hue and tweaking saturation/lightness
    const newH = (hsl.h + 180 + index * 30) % 360; // Rotate hue across the spectrum
    const newS = Math.max(30, Math.min(hsl.s + 10, 100)); // Slightly boost saturation
    const newL = Math.max(40, Math.min(hsl.l + 10, 80)); // Bring lightness to a comfortable range

    return hslToHex({ h: newH, s: newS, l: newL });
  });

  return generatedColors;
}
